/**
 * PasswordInput Component
 *
 * A specialized input component for password fields with visibility toggle,
 * existing value placeholder handling, and full ARIA accessibility support.
 *
 * Features:
 * - Toggle password visibility with Eye/EyeOff icons
 * - Shows placeholder when hasExistingValue is true and user hasn't edited
 * - Tracks internal hasBeenEdited state to distinguish user edits from placeholder
 * - Error state styling with red border
 * - Disabled state styling
 * - Full ARIA accessibility support
 */

import * as React from 'react';
import { useState, useId, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PASSWORD_MANAGER_OPT_OUT } from '@/components/connectors/shared/passwordManagerOptOut';

/**
 * Props for the PasswordInput component
 */
export interface PasswordInputProps {
  /** Current value of the input */
  value: string;
  /** Callback fired when the value changes */
  onChange: (value: string) => void;
  /** Indicates whether the server has a saved password value */
  hasExistingValue: boolean;
  /** Placeholder text shown when field is empty and no existing value */
  placeholder?: string;
  /** Error message to display - also triggers error styling */
  error?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** HTML id attribute for the input */
  id?: string;
  /** HTML name attribute for the input */
  name?: string;
  /** Accessible label for the input (used when no visible label) */
  ariaLabel?: string;
  /** Maximum character length for the input */
  maxLength?: number;
}

/** Placeholder displayed when there's an existing saved password */
const EXISTING_VALUE_PLACEHOLDER = '••••••••';

/**
 * PasswordInput - A password input field with visibility toggle and existing value handling
 *
 * When hasExistingValue is true and the user hasn't started editing,
 * the field displays a masked placeholder to indicate a value exists.
 * Once the user starts typing, the placeholder clears and shows the actual input.
 *
 * @example
 * ```tsx
 * <PasswordInput
 *   value={password}
 *   onChange={setPassword}
 *   hasExistingValue={hasPassword}
 *   error={errors.password}
 *   ariaLabel="Database password"
 * />
 * ```
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      value,
      onChange,
      hasExistingValue,
      placeholder,
      error,
      disabled = false,
      id,
      name,
      ariaLabel,
      maxLength,
    },
    ref
  ) => {
    // Track whether user has started editing the field
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    // Track password visibility state
    const [showPassword, setShowPassword] = useState(false);

    // Generate unique IDs for accessibility
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;

    /**
     * Handle input value changes
     * Sets hasBeenEdited to true on first change to clear the placeholder
     */
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasBeenEdited) {
          setHasBeenEdited(true);
        }
        onChange(e.target.value);
      },
      [hasBeenEdited, onChange]
    );

    /**
     * Toggle password visibility
     */
    const toggleVisibility = useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    /**
     * Determine the placeholder to show:
     * - If hasExistingValue and not yet edited: show masked placeholder
     * - Otherwise: show the provided placeholder or nothing
     */
    const displayPlaceholder =
      hasExistingValue && !hasBeenEdited ? EXISTING_VALUE_PLACEHOLDER : placeholder;

    /**
     * Determine the input type based on visibility state
     */
    const inputType = showPassword ? 'text' : 'password';

    return (
      <div className="relative">
        <Input
          ref={ref}
          id={inputId}
          name={name}
          type={inputType}
          {...PASSWORD_MANAGER_OPT_OUT}
          value={value}
          onChange={handleChange}
          placeholder={displayPlaceholder}
          disabled={disabled}
          maxLength={maxLength}
          aria-label={ariaLabel}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            'pr-10', // Make room for the toggle button
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={toggleVisibility}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </Button>
        {error && (
          <p id={errorId} className="mt-1.5 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
